<template>
    <div>
        <div v-if="loading" class="text-center" style="padding: 40px 0;">
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
        </div>
        <div v-else>
            <div v-if="city.slug && !city.is_coastal" class="text-center text-muted" style="padding: 40px 0 24px;">
                <p style="font-size: 1.5rem; margin-bottom: 8px;">🌊</p>
                <p class="mb-1 fw-semibold" style="color: #444;">Tides are only available for coastal cities.</p>
                <p class="mb-3" style="font-size: 0.875em;">{{ city.name }} is not on the coast. Try one of these nearby coastal cities:</p>
                <div v-if="nearbyCoastalCities.length">
                    <span v-for="item in nearbyCoastalCities" :key="item.id">
                        <a href="#" @click.prevent="selectCoastalCity(item)" class="badge bg-light text-dark border m-1" style="font-size: 0.85em;">{{ item.name }}</a>
                    </span>
                </div>
                <p v-else-if="!nearbyCoastalCities.length" class="mb-0 mt-2" style="font-size: 0.8em; color: #aaa;">No nearby coastal cities found.</p>
            </div>
            <div v-else-if="tides && !tides.coastal" class="text-center text-muted" style="padding: 40px 0;">
                <p style="font-size: 1.5rem; margin-bottom: 8px;">🌊</p>
                <p class="mb-1">No tidal data for this location.</p>
                <p class="mb-0" style="font-size: 0.875em;">Tide predictions are only available for coastal areas within 66°S–66°N.</p>
            </div>
            <div v-else-if="tides && tides.coastal">
                <div class="mb-3" style="border-radius: 8px; overflow: hidden;">
                    <TideChart :events="tides.events" title="Tide Times" :subtitle="chartSubtitle" :subtitle2="chartSubtitle2" />
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                        <thead>
                            <tr>
                                <th>Time<span v-if="tidesTimezoneAbbr" class="text-muted fw-normal ms-1" style="font-size:0.8em;">({{ tidesTimezoneAbbr }})</span></th>
                                <th>Tide</th>
                                <th>Height (MSL)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="event in tides.events" :key="event.minute_of_day">
                                <td>{{ event.time }}</td>
                                <td>
                                    <span v-if="event.type === 'High'" style="color: #0d6efd;">▲ High</span>
                                    <span v-else style="color: #0dcaf0;">▼ Low</span>
                                </td>
                                <td>{{ event.height_m.toFixed(2) }} m</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p class="text-muted mt-2 mb-0" style="font-size: 0.8em;">
                    Heights are relative to mean sea level (MSL), not chart datum. Best accuracy for open-coast locations — semi-enclosed water bodies (straits, bays, harbours) may show timing errors of several hours.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import astroTabMixin from '@/mixins/astroTab'
import astra from '@/api/astra'
import locci from '@/api/locci'
import TideChart from './TideChart'

export default {
    name: 'TabTides',
    mixins: [astroTabMixin],
    components: { TideChart },
    props: {
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            tides: null,
            nearbyCoastalCities: [],
        }
    },
    computed: {
        chartSubtitle() {
            if (!this.city || !this.city.name) return ''
            const parts = [this.city.name]
            if (this.city.region_name) parts.push(this.city.region_name)
            return 'near ' + parts.join(', ')
        },
        chartSubtitle2() {
            if (!this.date) return ''
            return 'for ' + this.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        },
        tidesTimezoneAbbr() {
            if (!this.tides?.tz) return ''
            try {
                const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: this.tides.tz })
                    .formatToParts(new Date())
                const part = parts.find(p => p.type === 'timeZoneName')
                return part ? part.value : ''
            } catch { return '' }
        },
    },
    watch: {
        active(val) { if (val) this.load() },
        city()      { if (this.active) this.load() },
        date()      { if (this.active) this.load() },
    },
    mounted() {
        if (this.active) this.load()
    },
    methods: {
        selectCoastalCity(city) {
            locci.fetchCityDatetime(city.slug)
                .then(r => { this.$store.commit('setCity', { ...city, timezone_slug: r.data.results.timezone_slug }) })
                .catch(() => { this.$store.commit('setCity', city) })
        },
        async load() {
            if (!this.latitude || !this.longitude || !this.timezone) return
            if (this.apiLocked) return
            this.loading = true
            this.tides = null
            if (!this.city || !this.city.is_coastal) {
                this.nearbyCoastalCities = []
                locci.findNearbyCoastal(this.city.slug)
                    .then(r => { this.nearbyCoastalCities = r.data.results || [] })
                    .catch(() => {})
                this.loading = false
            } else {
                await Promise.allSettled([
                    astra.fetchTides({ ...this.loc, date1: this.dailyRange.date1 })
                        .then(r => { this.tides = r.data })
                        .catch(() => {}),
                ])
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.table-responsive td,
.table-responsive th { white-space: nowrap; }
</style>
